import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './ChangeRole.css';

interface Lot {
  id: string | number;
  nom: string;
  code: number;
  description: string;
  isActive: boolean;
}

const ChangeRoleSection: React.FC<{ organismeId: number }> = ({ organismeId }) => {
  // Récupérer les informations utilisateur depuis le contexte d'authentification
  const { user } = useAuth();
  
  // Vérifier si l'utilisateur peut modifier les droits
  const canEditRights = user?.role === 'Acheteur signataire admin' || user?.role === 'Acheteur signataire';
  const [isValidateurAll, setIsValidateurAll] = useState(true);
  const [lots, setLots] = useState<Lot[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLotsAndPermissions = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Aucun token d\'authentification trouvé');
        setLoading(false);
        return;
      }

      // Récupérer tous les lots
      const lotsRes = await fetch(`${process.env.REACT_APP_API_URL}/lots-technique`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      const lotsData = await lotsRes.json();

      // Récupérer les permissions pour l'organisme
      const permRes = await fetch(`${process.env.REACT_APP_API_URL}/lots-permissions/organisme/${organismeId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      const permData = await permRes.json();

      // Fusionner les lots et les permissions
      const lotsFromApi = Array.isArray(lotsData.data)
        ? lotsData.data.map((lot: any) => {
          const perm = Array.isArray(permData.data)
            ? permData.data.find((p: any) => p.id === lot.id)
            : null;
          return {
            id: lot.id,
            nom: lot.nom,
            code: lot.code,
            description: lot.description,
            isActive: perm ? perm.has_access : false,
          };
        })
        : [];

      setLots(lotsFromApi);
      setIsValidateurAll(lotsFromApi.length > 0 && lotsFromApi.every(lot => lot.isActive));
      setLoading(false);
    };

    fetchLotsAndPermissions();
  }, [organismeId]);

  // Toggle tous les lots
  const handleToggleAll = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const newVal = !isValidateurAll;
    setIsValidateurAll(newVal);
    setLots(lots.map(lot => ({ ...lot, isActive: newVal })));

    // Appel API pour chaque lot
    await Promise.all(
      lots.map(lot =>
        fetch(`${process.env.REACT_APP_API_URL}/lots-permissions/toggle`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            id_lot_technique: lot.id,
            id_organisme: organismeId,
            desiredState: newVal
          })
        })
      )
    );
  };

  // Toggle individuel
  const handleToggleLot = async (id: string | number) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const updatedLots = lots.map(lot =>
      lot.id === id ? { ...lot, isActive: !lot.isActive } : lot
    );
    setLots(updatedLots);
    const toggledLot = updatedLots.find(lot => lot.id === id);
    if (!toggledLot) return;

    // Appel API pour ce lot
    await fetch(`${process.env.REACT_APP_API_URL}/lots-permissions/toggle`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        id_lot_technique: toggledLot.id,
        id_organisme: organismeId,
        desiredState: toggledLot.isActive
      })
    });

    // Met à jour le switch global
    const allActive = updatedLots.length > 0 && updatedLots.every(lot => lot.isActive);
    setIsValidateurAll(allActive);
  };

  return (
    <section className="change-role-modal">
      <h2>L'organisme peut passer commande sur: </h2>
      <div className="lots-list">
        <div className="lot-item lot-item-title">
          <label className="switch-label">
            <h3>Tous les lots:</h3>
            <div className="switch">
              <input
                type="checkbox"
                checked={isValidateurAll}
                onChange={handleToggleAll}
                disabled={loading || !canEditRights}
              />
              <span className="slider"></span>
            </div>
          </label>
          <hr />
        </div>

        {loading ? (
          <div>
            Chargement des lots...
          </div>
        ) : lots.length > 0 ? (
          lots.map(lot => (
            <div key={lot.id} className="lot-item">
              <label className="switch-label">
                <span>{lot.code} - {lot.description}</span>
                <div className="switch">
                  <input
                    type="checkbox"
                    checked={lot.isActive}
                    onChange={() => handleToggleLot(lot.id)}
                    disabled={loading || !canEditRights}
                  />
                  <span className="slider"></span>
                </div>
              </label>
            </div>
          ))
        ) : (
          <div>
            Aucun lot technique disponible
          </div>
        )}
      </div>
      {/* Message pour les utilisateurs sans droit de modification */}
      {!canEditRights && (
        <div className="info-message">
          <p>Vous êtes en mode consultation. Seuls les administrateurs et les signataires peuvent modifier les droits.</p>
        </div>
      )}
    </section>
  );
};

export default ChangeRoleSection;